/**
 * TeacherVerificationPanel — goedkeuren of afwijzen van een leerkracht.
 *
 * WAAROM DIT EEN EIGEN PANEEL IS EN GEEN KNOP IN DE MELDING
 *
 * `AccountStatusNotice` zegt wat er aan de hand is. Dit paneel is de handeling,
 * en die verdient een eigen plek omdat hij iets anders is dan de rest van dit
 * scherm: bij verifiëren verklaart een beheerder dat hij iets buiten dit scherm
 * heeft gecontroleerd. Een losse knop naast een waarschuwing nodigt uit tot
 * wegklikken van de waarschuwing, niet tot controleren.
 *
 * DAAROM DE CHECKLIST
 *
 * De drie punten zijn geen formaliteit maar de enige inhoud van de handeling —
 * de database kan niets van dit alles zelf vaststellen. Ze staan er als tekst
 * en niet als aanvinkvakjes: vakjes worden aangeklikt, een zin wordt gelezen.
 * En het is bewust géén vrij tekstveld zoals bij break glass. Daar leg je vast
 * *waarom je kijkt*; hier bevestig je *dat je iets hebt gedaan*, en dat is niet
 * met een zelfgeschreven regel te onderbouwen.
 *
 * WAAROM AFWIJZEN NIET VERSTOPT IS
 *
 * Afwijzen is de faalstand van dit proces en hoort even bereikbaar te zijn als
 * goedkeuren. Wordt afwijzen lastiger dan goedkeuren, dan wordt er goedgekeurd.
 */

import React, { useState } from 'react';
import { ShieldCheck, ShieldX, AlertTriangle } from 'lucide-react';
import { verifyTeacher } from '../../../../data/supabase/teacherVerification';
import type { DirectoryAccount } from '../../../../data/supabase/adminDirectory';
interface TeacherVerificationPanelProps {
  account: DirectoryAccount;
  /** Ververst de serverlijst zodat de nieuwe status meteen zichtbaar is. */
  onChanged: () => void;
}
export function TeacherVerificationPanel({
  account,
  onChanged
}: TeacherVerificationPanelProps) {
  const [busy, setBusy] = useState<null | 'approve' | 'reject'>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<null | 'approve' | 'reject'>(null);
  if (account.role !== 'teacher') return null;
  if (account.teacherStatus !== 'pending') return null;
  const run = async (approve: boolean) => {
    setBusy(approve ? 'approve' : 'reject');
    setError(null);
    const res = await verifyTeacher(account.id, approve);
    setBusy(null);
    if (!res.ok) {
      setError(res.message ?? 'Er is niets gewijzigd.');
      return;
    }
    setDone(approve ? 'approve' : 'reject');
    onChanged();
  };
  if (done) {
    return <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-4" data-id="element-7904" data-mp="303qfr7hprkrs">
        <p className="text-sm font-bold text-slate-900" data-id="element-7905" data-mp="3531wwhlzss9w">
          {done === 'approve' ? 'Geverifieerd' : 'Afgewezen'}
        </p>
        <p className="mt-1 text-sm leading-relaxed text-slate-600" data-id="element-7906" data-mp="2ravhixqnypbi">
          {done === 'approve' ? 'Deze leerkracht komt nu bij de leerlingen van de klassen waar hij lid van is. Je naam en het tijdstip staan bij deze beslissing vastgelegd.' : 'Deze leerkracht krijgt geen toegang tot leerlinggegevens. Het account blijft bestaan; opheffen is een aparte handeling.'}
        </p>
      </div>;
  }
  return <section className="mb-4 rounded-xl border border-slate-200 bg-white p-4" aria-labelledby="verify-heading" data-id="element-7907" data-mp="1cdsiz7nmlmuo">
      <h3 id="verify-heading" className="text-sm font-bold text-slate-900" data-id="element-7908" data-mp="17ezhyepizobe">
        Verificatie afhandelen
      </h3>
      <p className="mt-1 text-sm leading-relaxed text-slate-600" data-id="element-7909" data-mp="1m3vkjw8tc94m">
        Goedkeuren geeft deze persoon toegang tot de gegevens van kinderen die
        niet van hem zijn. Doe dit alleen als je buiten dit scherm hebt
        vastgesteld dat:
      </p>
      <ul className="mt-2 space-y-1 text-sm text-slate-600" data-id="element-7910" data-mp="oheosvfqa3kz">
        <li className="flex gap-2" data-id="element-7911" data-mp="oj9d8jq7kdtt">
          <span aria-hidden="true" className="text-slate-400" data-id="element-7912" data-mp="13ep2mbfufljv">
            •
          </span>
          de school geverifieerd in het register staat;
        </li>
        <li className="flex gap-2" data-id="element-7913" data-mp="yhfdkwwd9bwk">
          <span aria-hidden="true" className="text-slate-400" data-id="element-7914" data-mp="2est0vttgrvuq">
            •
          </span>
          deze persoon daar werkt;
        </li>
        <li className="flex gap-2" data-id="element-7915" data-mp="29tyb27x2x714">
          <span aria-hidden="true" className="text-slate-400" data-id="element-7916" data-mp="jd493ejasxh7">
            •
          </span>
          je dat via de school hebt bevestigd, niet via het opgegeven
          e-mailadres.
        </li>
      </ul>

      {/*
       Het gegeven waarop de hele beslissing rust, dus het staat er als eigen
       blok en niet als regeltje tussen de andere velden. Leeg is een geldige
       en BETEKENISVOLLE waarde — geen ontbrekend gegeven maar een andere
       situatie, en die vraagt een ander antwoord dan goedkeuren.
       */}
      {/*
       DE HERKOMST IS BELANGRIJKER DAN DE NAAM.
        Sinds 0041 bestaat het scholenregister, en daarmee is er verschil tussen
       een school die de leerkracht KOOS uit onze lijst en een naam die hij zelf
       intikte. Alleen de eerste is natrekbaar. Zou hier alleen de naam staan,
       dan zou een aanvraagtekst er precies zo gezaghebbend uitzien als een
       geverifieerde school — en dan is dit paneel weer een handeling die
       eruitziet als een controle.
       */}
      {account.schoolName?.trim() ? <div className={`mt-3 rounded-lg border p-3 ${account.schoolSource === 'registry' ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`} data-id="element-7917" data-mp="2072mwyblxr31">
          <p className="text-xs font-bold uppercase tracking-wide text-slate-500" data-id="element-7918" data-mp="1q6sahfxecnox">
            {account.schoolSource === 'registry' ? 'Gekozen uit het register' : 'Zelf ingetypt — aanvraag'}
          </p>
          <p className="mt-0.5 text-sm font-bold text-slate-900" data-id="element-7919" data-mp="2iw8xtz65teea">
            {account.schoolName}
          </p>
          <p className="mt-1 text-xs leading-relaxed text-slate-600" data-id="element-7920" data-mp="2dxxpjsvijby6">
            {account.schoolSource === 'registry' ? 'Deze school staat geverifieerd in ons register; de verwerkersovereenkomst is daar vastgelegd. Wat je hier nog vaststelt is of déze persoon er werkt.' : account.schoolSource === 'request' ? 'Deze school staat niet in het register. De naam is door de leerkracht zelf opgeschreven en dus een bewering. Handel eerst de aanvraag af bij Scholen — daarna verifieer je de persoon.' : 'De herkomst van deze naam is niet bekend. Dat betekent dat het account is aangemaakt voordat het scholenregister bestond; behandel de naam als een bewering.'}
          </p>
        </div> : <p className="mt-3 flex gap-2 rounded-lg bg-amber-50 p-2.5 text-xs leading-relaxed text-amber-900" data-id="element-7921" data-mp="l8y9stschjzq">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" aria-hidden="true" data-id="element-7922" data-mp="q6q6s27ujwas" />
          <span data-id="element-7923" data-mp="c6p36f076arm">
            <strong data-id="element-7924" data-mp="c6xdbz4xr1z4">Geen school opgegeven.</strong> Dit is een zelfstandige
            leerkracht (bijles, thuisonderwijs) — of een leerkracht die zich
            vóór 16 augustus registreerde, toen de school nog niet werd bewaard.
            Zonder school achter zich hoort toegang via de ouder van een kind te
            lopen, niet via verificatie. Keur alleen goed als je weet welke van
            de twee het is.
          </span>
        </p>}

      {error && <p role="alert" className="mt-3 text-sm font-semibold text-rose-700" data-id="element-7925" data-mp="21meuivsxczcn">
          {error}
        </p>}

      <div className="mt-4 flex flex-wrap gap-2" data-id="element-7926" data-mp="3m45znirw4l5l">
        <button onClick={() => void run(true)} disabled={busy !== null} className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-bold text-white transition-colors hover:bg-emerald-700 disabled:opacity-60" data-id="element-7927" data-mp="3m2mlpytpim6z">
          <ShieldCheck className="h-4 w-4" data-id="element-7928" data-mp="3r3a98aiq8kwg" />
          {busy === 'approve' ? 'Bezig…' : 'Goedkeuren'}
        </button>
        <button onClick={() => void run(false)} disabled={busy !== null} className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 bg-white px-3 py-2 text-sm font-bold text-rose-700 transition-colors hover:bg-rose-50 disabled:opacity-60" data-id="element-7929" data-mp="8ormx19rxffi">
          <ShieldX className="h-4 w-4" data-id="element-7930" data-mp="dow3eraoma5q" />
          {busy === 'reject' ? 'Bezig…' : 'Afwijzen'}
        </button>
      </div>
    </section>;
}